import * as THREE from "three";
import { weatherAt, weatherFromClimate, type PlanetDef, type PlanetSampler, type TerrainSample, type WeatherColumn } from "@hyperlight/shared";

export type WeatherPreset = "clear" | "fair" | "overcast" | "rain" | "snow";
export const WEATHER = {
  preset: null as WeatherPreset | null,
  volumetrics: true,
  quality: "low" as "low" | "high",
  strength: 1,
};
let clock = 0;
let reviewTime: number | null = null;
/** DEV atlas hook; null restores the shared simulation clock. */
export function setWeatherReviewTime(t: number | null): void {
  reviewTime = t !== null && Number.isFinite(t) ? t : null;
}
export function setWeatherTime(t: number): void { clock = t; }
export function weatherTime(): number { return reviewTime ?? clock; }
export function emptyWeather(): WeatherColumn {
  return { cloud: 0, precip: 0, kind: 0, windx: 0, windy: 0, windz: 0, windSpeed: 0, cloudDepth: 0, snowFraction: 0, mist: 0 };
}
/** Debug overrides are applied to every consumer, including the planet map. */
export function applyWeatherPreset(out: WeatherColumn, def: PlanetDef): WeatherColumn {
  if (!WEATHER.preset || !def.atmosphere) return out;
  const p = WEATHER.preset;
  out.cloud = p === "clear" ? 0.08 : p === "fair" ? 0.35 : p === "overcast" ? 0.86 : 1;
  out.precip = p === "rain" || p === "snow" ? 0.8 : 0;
  out.kind = p === "snow" ? 2 : p === "rain" ? 1 : 0;
  out.snowFraction = p === "snow" ? 1 : 0;
  out.cloudDepth = p === "clear" ? 0.08 : p === "fair" ? 0.3 : p === "overcast" ? 1.8 : 3.8;
  out.mist = p === "clear" ? 0.05 : p === "fair" ? 0.1 : p === "overcast" ? 0.22 : 0.65;
  return out;
}
export function sampleWeather(sampler: PlanetSampler, def: PlanetDef, x: number, y: number, z: number, t: number, out: WeatherColumn): WeatherColumn {
  return applyWeatherPreset(weatherAt(sampler, def, x, y, z, reviewTime ?? t, out), def);
}

export const WEATHER_MAP_W = 64, WEATHER_MAP_H = 32;
const INTERVAL = 4;
const climate: TerrainSample = { h: 0, temp: 0, moist: 0, river: 0, mountain: 0, flowx: 0, flowy: 0, flowz: 0, town: 0, road: 8 };
const sample = emptyWeather();

/** Low-frequency spherical weather, sampled on CPU from the authoritative field.
 * Two timestamped snapshots interpolate continuously. Static climate is sampled
 * once per texel, with no terrain work during subsequent updates. */
export class WeatherField {
  readonly uniforms = {
    uWeatherA: { value: null as THREE.Texture | null },
    uWeatherB: { value: null as THREE.Texture | null },
    uWeatherMix: { value: 0 },
  };
  private readonly climate = new Float32Array(WEATHER_MAP_W * WEATHER_MAP_H * 5);
  private maps: THREE.DataTexture[] = [];
  private epoch = NaN;
  private prepared = 0;
  private preset: WeatherPreset | null = null;
  constructor(readonly def: PlanetDef, private readonly sampler: PlanetSampler) {}

  private init(): void {
    if (this.maps.length) return;
    for (let y = 0; y < WEATHER_MAP_H; y++) for (let x = 0; x < WEATHER_MAP_W; x++) {
      const lon = ((x + 0.5) / WEATHER_MAP_W - 0.5) * Math.PI * 2;
      const lat = ((y + 0.5) / WEATHER_MAP_H - 0.5) * Math.PI;
      const dx = Math.cos(lat) * Math.cos(lon), dy = Math.sin(lat), dz = Math.cos(lat) * Math.sin(lon);
      this.sampler.sample(dx, dy, dz, climate, true);
      this.climate.set([dx, dy, dz, climate.temp, climate.moist], (y * WEATHER_MAP_W + x) * 5);
    }
    for (let i = 0; i < 3; i++) {
      const tex = new THREE.DataTexture(new Uint8Array(WEATHER_MAP_W * WEATHER_MAP_H * 4), WEATHER_MAP_W, WEATHER_MAP_H);
      tex.minFilter = tex.magFilter = THREE.LinearFilter;
      tex.wrapS = THREE.RepeatWrapping;
      tex.wrapT = THREE.ClampToEdgeWrapping;
      tex.generateMipmaps = false;
      this.maps.push(tex);
    }
  }
  private fill(tex: THREE.DataTexture, time: number, start = 0, count = WEATHER_MAP_W * WEATHER_MAP_H): void {
    const data = tex.image.data as Uint8Array;
    const end = Math.min(WEATHER_MAP_W * WEATHER_MAP_H, start + count);
    for (let i = start; i < end; i++) {
      const j = i * 5, c = this.climate;
      if (!WEATHER.preset || !this.def.atmosphere) weatherFromClimate(this.def, c[j], c[j + 1], c[j + 2], time, c[j + 3], c[j + 4], sample);
      applyWeatherPreset(sample, this.def);
      data[i * 4] = Math.round(sample.cloud * 255);
      data[i * 4 + 1] = Math.round(sample.precip * 255);
      data[i * 4 + 2] = Math.round(Math.min(1, (sample.cloudDepth ?? 0) / 5) * 255);
      data[i * 4 + 3] = Math.round((sample.snowFraction ?? 0) * 255);
    }
    if (end === WEATHER_MAP_W * WEATHER_MAP_H) tex.needsUpdate = true;
  }
  update(time: number): void {
    time = reviewTime ?? time;
    this.init();
    const epoch = Math.floor(time / INTERVAL) * INTERVAL;
    if (epoch !== this.epoch || this.preset !== WEATHER.preset) {
      if (epoch === this.epoch + INTERVAL && this.preset === WEATHER.preset) {
        // Catch up after a suspended tab; never display a partly written map.
        this.fill(this.maps[2], epoch + INTERVAL, this.prepared);
        this.maps.push(this.maps.shift()!);
      } else {
        this.fill(this.maps[0], epoch);
        this.fill(this.maps[1], epoch + INTERVAL);
      }
      this.epoch = epoch;
      this.preset = WEATHER.preset;
      this.prepared = 0;
    }
    this.uniforms.uWeatherA.value = this.maps[0];
    this.uniforms.uWeatherB.value = this.maps[1];
    this.uniforms.uWeatherMix.value = (time - epoch) / INTERVAL;
    if (this.prepared < WEATHER_MAP_W * WEATHER_MAP_H) {
      this.fill(this.maps[2], epoch + INTERVAL * 2, this.prepared, 64);
      this.prepared += 64;
    }
  }
  dispose(): void { for (const tex of this.maps) tex.dispose(); this.maps = []; this.epoch = NaN; }
}

export const WEATHER_MAP_GLSL = /* glsl */ `
uniform sampler2D uWeatherA, uWeatherB;
uniform float uWeatherMix;
vec4 weatherMap(vec3 direction) {
  vec3 d = normalize(direction);
  vec2 uv = vec2(atan(d.z, d.x) / 6.28318530718 + 0.5, asin(clamp(d.y, -1.0, 1.0)) / 3.14159265359 + 0.5);
  return mix(texture2D(uWeatherA, uv), texture2D(uWeatherB, uv), uWeatherMix);
}
`;
